// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { Moves, Moves_Order_By } from '@gql/base-types';
import { useGetMovesMissingRefQuery } from '../queries/getMovesMissingRef.query.generated';
import { useGetMovesMissingRefCountsQuery } from '../queries/getMovesMissingRefCounts.query.generated';
import { useGetMovesMissingRefPoolsQuery } from '../queries/getMovesMissingRefPools.query.generated';
import { useGetServiceStopsMissingRoQuery } from '../queries/getServiceStopsMissingRo.query.generated';

import { useUser, useRooftop } from '@store';
import { useProductEntry } from '@features/products/useProductEntry';
import { isProductHeld } from '@features/products/productGates';
import { useMissingRefReport } from '@features/missingRefReport/providers/MissingRefReportProvider';
import { buildMissingRefWhereVariables } from '@features/missingRefReport/utils/buildMissingRefWhereClause';

import MissingRefReportContent from '@features/missingRefReport/components/MissingRefReportContent';

// COMPONENT ---------------------------------------------------------------- //

const MissingRefReportQuery = () => {
  // Context variables
  const { end, limit, metric, offset, order, orderBy, search, start, usernames, lockedView } = useMissingRefReport();

  // Store variables
  const user = useUser();
  const rooftop = useRooftop();
  const mobileServiceHeld = isProductHeld(useProductEntry('mobile-service'));
  // Fetched only when it can be shown.
  const serviceStopsRelevant = mobileServiceHeld && lockedView !== 'moves';

  // Get customerId from rooftop selection (same pattern as Dashboard)
  const [customerId, setCustomerId] = React.useState<number>(rooftop?.id || user?.profile?.customer_id || 0);

  const [moves, setMoves] = React.useState<Moves[]>([]);
  const [aggregateCounts, setAggregateCounts] = React.useState<{
    allMoves: number;
    upcomingMoves: number;
    inProgressMoves: number;
    completedMoves: number;
    canceledMoves: number;
  }>({
    allMoves: 0,
    upcomingMoves: 0,
    inProgressMoves: 0,
    completedMoves: 0,
    canceledMoves: 0,
  });
  const [usernamesPool, setUsernamesPool] = React.useState<string[]>([]);

  // Update customerId when rooftop changes
  React.useEffect(() => {
    let newCustomerId: number = 0;
    if (rooftop?.id) {
      newCustomerId = rooftop.id;
    } else if (user?.profile?.customer_id) {
      newCustomerId = user.profile.customer_id;
    }
    setCustomerId(newCustomerId);
  }, [rooftop, user]);

  // Convert order/orderBy to GraphQL format
  const orderByGraphQL = React.useMemo(() => {
    const orderByArray = [];

    // Check for an orderBy
    if (orderBy) {
      // If the orderBy contains a nested field, build the nested orderBy structure
      if (orderBy.includes('.')) {
        const [parentField, childField] = orderBy.split('.');
        orderByArray.push({ [parentField]: { [childField]: `${order}_nulls_last` } });
      }
      // Otherwise, build the simple orderBy structure
      else {
        orderByArray.push({ [orderBy]: `${order}_nulls_last` });
      }
    }

    // Always add ready_by and id to the end of the array
    orderByArray.push({ ready_by: `desc` });
    orderByArray.push({ id: `desc` });

    // Return the array as Moves_Order_By[]
    return orderByArray as Moves_Order_By[];
  }, [orderBy, order]);

  // Build WHERE clause variables based on selected metric and search term
  const whereVariables = React.useMemo(() => {
    return buildMissingRefWhereVariables({ usernames, search, metric });
  }, [usernames, search, metric]);

  // Fetch moves
  const { loading, error, data, refetch } = useGetMovesMissingRefQuery({
    variables: {
      start,
      end,
      customerId,
      ...whereVariables,
      orderBy: orderByGraphQL,
      limit,
      offset,
    },
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !customerId, // Skip if no customer selected
  });

  // Fetch counts
  const { data: countsData, refetch: countsRefetch } = useGetMovesMissingRefCountsQuery({
    variables: {
      start,
      end,
      customerId,
    },
    pollInterval: 10000,
    fetchPolicy: 'no-cache',
    skip: !customerId,
  });

  // Fetch pools
  const { data: poolsData, refetch: poolsRefetch } = useGetMovesMissingRefPoolsQuery({
    variables: {
      start,
      end,
      customerId,
    },
    skip: !customerId,
  });

  // Fetch mobile service stops missing an RO # (unpaginated - counts are expected to be small)
  const {
    data: stopsData,
    loading: stopsLoading,
    refetch: stopsRefetch,
  } = useGetServiceStopsMissingRoQuery({
    variables: {
      start,
      end,
      customerId,
    },
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !customerId || !serviceStopsRelevant,
  });

  // Update moves when data arrives
  React.useEffect(() => {
    if (data) {
      setMoves(data?.moves as Moves[]);
    }
  }, [data]);

  // Update counts when data arrives
  React.useEffect(() => {
    if (countsData) {
      setAggregateCounts({
        allMoves: countsData?.allMoves?.aggregate?.count || 0,
        upcomingMoves: countsData?.upcomingMoves?.aggregate?.count || 0,
        inProgressMoves: countsData?.inProgressMoves?.aggregate?.count || 0,
        completedMoves: countsData?.completedMoves?.aggregate?.count || 0,
        canceledMoves: countsData?.canceledMoves?.aggregate?.count || 0,
      });
    }
  }, [countsData]);

  // Update pools when data arrives
  React.useEffect(() => {
    if (poolsData) {
      // Usernames pool
      let newUsernamesPool = poolsData?.moves?.map(move => move?.createdBy || '') || [];
      newUsernamesPool = [...new Set(newUsernamesPool)];
      newUsernamesPool = newUsernamesPool?.filter(u => Boolean(u) && u?.includes('@'));
      newUsernamesPool = newUsernamesPool?.sort();
      setUsernamesPool(newUsernamesPool);
    }
  }, [poolsData]);

  // Handle refetching the data
  const handleRefetch = async () => {
    await Promise.all([refetch(), countsRefetch(), poolsRefetch(), ...(serviceStopsRelevant ? [stopsRefetch()] : [])]);
  };

  return (
    <MissingRefReportContent
      loading={loading}
      error={error}
      refetch={handleRefetch}
      moves={moves}
      aggregateCounts={aggregateCounts}
      usernamesPool={usernamesPool}
      serviceStops={serviceStopsRelevant ? stopsData?.mobility_stops || [] : []}
      serviceStopsCount={serviceStopsRelevant ? stopsData?.stopsMissingRoCount?.aggregate?.count || 0 : 0}
      serviceStopsLoading={serviceStopsRelevant ? stopsLoading : false}
      showServiceStops={serviceStopsRelevant}
    />
  );
};

// EXPORT ---------------------------------------------------------------- //

export default MissingRefReportQuery;
